import * as React from 'react'
import axios from 'axios'
import queryString from 'query-string'
import { useLocale, useTranslation } from 'hooks'
import { updateDialingCode, withTranslations } from 'helpers'

/**
 * A React component for selecting a country.
 * @param {CountrySelectProps} props - The props for the component.
 * @returns {JSX.Element} - The rendered component.
 */
const CountrySelect = ({
  onChange,
  onCountriesLoaded,
  displayLabel,
  include = 'Supported',
  priorityCodes = ['GB', 'FR', 'DE', 'IE', 'US', 'IT', 'NL'],
  blankOption = false,
  displayDelivery = false,
  value,
  newForm = false,
  modelName,
  // Form field this select writes into. Defaults to "country" so the
  // existing Product / Address callers (whose column literally IS
  // `country`) keep working without touching every callsite. Brand
  // edit, GTIN form, and any future caller editing a differently-named
  // column (e.g. `default_country_of_origin`) pass an explicit value
  // so the form submission actually lands in the permitted-params slot.
  fieldName = 'country',
  disabled = false,
  dontUpdateDialingCode = false,
  onCountryCodeChange
}) => {
  const [countries, setCountries] = React.useState(
    /** @type {Country[]} */ ([])
  )
  // Internal selection state, seeded from the `value` prop. Without
  // this the <select> was controlled but had no setState path —
  // user picks a new country, React re-renders, value prop hasn't
  // changed, React snaps the displayed value back. Tracking
  // internally + keeping the underlying form input's name set means
  // form submission picks up the user's choice while React's render
  // matches what the user sees.
  const [selectedValue, setSelectedValue] = React.useState(value || '')

  // If the parent re-renders with a new `value` prop (e.g. after a
  // save), pick it up. Doesn't interfere with normal user-driven
  // selection because the user's change updates selectedValue
  // immediately and the value prop only changes on parent re-render.
  React.useEffect(() => {
    setSelectedValue(value || '')
  }, [value])

  const { t } = useTranslation()
  const { currency, localiseUrl } = useLocale()

  const countriesLoaded = countries.length > 0

  const priorityCountries = countries.filter(
    (/**@type {Country} */ country) => priorityCodes.indexOf(country.code) >= 0
  )

  React.useEffect(() => {
    const query = include
      ? `?${queryString.stringify({ filter: include })}`
      : ''

    axios
      .get(
        localiseUrl(
          `/api/countries${query}&with_shipping_fees=${displayDelivery}&currency=${currency}`,
          { localeAsParam: true }
        )
      )
      .then(({ data }) => {
        setCountries(data.countries)
        if (onCountriesLoaded) {
          onCountriesLoaded(data.countries)
        }

        if (!dontUpdateDialingCode) {
          updateDialingCode(value)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * @param {Object} event
   * @param {HTMLSelectElement} event.target
   */
  const handleChange = ({ target }) => {
    setSelectedValue(target.value)

    if (!dontUpdateDialingCode && onCountryCodeChange) {
      onCountryCodeChange(target.value)
    }

    if (onChange) {
      const country = countries.find((c) => c.code === target.value)

      onChange(target.value, country)
    }
  }

  /**
   * @param {Country} country - The country object containing code, name and fee
   * @param {string} key - The key to use for the option element
   * @returns {JSX.Element} - The label element containing country name and delivery information
   */
  const countryLabel = (country, key) => {
    const deliveryString = displayDelivery
      ? ` (${country.fee} ${t('react_components.address.delivery')})`
      : ''

    return (
      <option key={`country-option-${key}`} value={country.code}>
        {country.name}
        {deliveryString}
      </option>
    )
  }

  return (
    <div className={`${newForm ? 'new-field' : ''} field field--select`}>
      <label className={newForm ? 'new-label' : ''} htmlFor={fieldName}>
        {displayLabel || t('react_components.address.country')}
      </label>
      <select
        name={`${modelName}[${fieldName}]`}
        className={newForm ? 'new-input' : ''}
        id={fieldName}
        value={countriesLoaded ? selectedValue : 'loading'}
        onChange={handleChange}
        disabled={disabled}
      >
        {countriesLoaded ? (
          <>
            {blankOption && (
              <option value="" disabled selected>
                {t('react_components.address.please_select', {
                  label: displayLabel || t('react_components.address.country')
                })}
              </option>
            )}
            {priorityCountries.map((country, i) =>
              countryLabel(country, `priority-${i}`)
            )}
            {countries.map((country, i) => countryLabel(country, `${i}`))}
          </>
        ) : (
          <option disabled value="loading">
            {t('shared.loading')}
          </option>
        )}
      </select>
    </div>
  )
}

/**
 * @typedef {Object} Country
 * @property {string} code - The country code.
 * @property {string} name - The country name.
 * @property {number} fee - The delivery fee for the country.
 * @property {number} numeric_code - country numeric code
 */
/**
 * @typedef {import('./types').CountrySelectProps} CountrySelectProps
 */

export default withTranslations(CountrySelect)
